import { Box, Link, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ksyos/design-system';
import type { IInsurerServiceDeclarationRule } from '@frontend/components/interfaces';
import { ManagerAuthorization } from '@common/enums';
import { DeleteButton, EditButton, ViewButton } from '@frontend/components/elements/common';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';

type InsurerServiceDeclarationRulesListTableProps = {
    items: IInsurerServiceDeclarationRule[];
    onRemove: (id: string) => void;
    page: number;
    serviceId: string;
};

export function InsurerServiceDeclarationRulesListTable({
    items,
    onRemove,
    page,
    serviceId,
}: InsurerServiceDeclarationRulesListTableProps) {
    const { common, insurers } = getSelectedLanguage();

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableHeader>{common.name}</TableHeader>
                    <TableHeader>{insurers.exportUntilDate}</TableHeader>
                </TableRow>
            </TableHead>
            <TableBody>
                {Boolean(items.length) &&
                    items.map((item) => {
                        return (
                            <TableRow key={item.id}>
                                <TableCell>
                                    {item.authorization.canUpdate && (
                                        <Link
                                            to={`/insurers/${item.insurer.id}`}
                                            variant="link"
                                        >
                                            {item.insurer.name}
                                        </Link>
                                    )}
                                    {!item.authorization.canUpdate && item.insurer.name}
                                </TableCell>
                                <TableCell>
                                    <NullableFormattedDate value={item.exportUntilDate} />
                                </TableCell>
                                <TableCell>
                                    <Box
                                        textAlign="right" // box is required because of TableCell align prop bug in design system https://ksyos-it.atlassian.net/browse/CS-22430
                                    >
                                        {renderTableButtons(item)}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
            </TableBody>
        </Table>
    );

    function renderTableButtons(item: IInsurerServiceDeclarationRule) {
        const { canUpdate, canDelete } = item.authorization;
        const canInsurerRead = hasOneAuthorization(ManagerAuthorization.insurersRead);
        const insurerCannotAddEdit = !hasOneAuthorization(ManagerAuthorization.insurersAddEdit);

        const insurerId = item.insurer.id;

        const baseURL = `/insurerServiceDeclarationRules/${item.id}`;
        const editURL = `${baseURL}/edit?serviceId=${serviceId}&returnPage=${page}`;
        const viewURL = `${baseURL}?serviceId=${serviceId}&insurerId=${insurerId}&returnPage=${page}`;

        return (
            <>
                {canInsurerRead && insurerCannotAddEdit && <ViewButton to={viewURL} />}
                {canUpdate && <EditButton to={editURL} />}
                {canDelete && <DeleteButton onClick={() => onRemove(item.id)} />}
            </>
        );
    }
}
